import React, { useEffect, useRef, useState } from 'react';
import { Eye, LogOut } from 'lucide-react';
import api from '../../services/api';

/**
 * The bar that says whose account you are looking at.
 *
 * Fixed to the top, never dismissible, and it pushes the app down rather than
 * floating over it. Somebody who forgets they are viewing as another user is
 * the failure mode this whole feature has to avoid, so the reminder is not
 * something that can be tidied away.
 *
 * THE PUSH IS REAL NOW. That sentence was an intention: the bar was
 * `position: fixed` and rendered nothing behind it, so it sat ON the first
 * thirty-one pixels of the application and the menu button underneath it could
 * not be clicked — you could see the bar and not the hamburger it was covering.
 *
 * Measured rather than assumed: the text wraps to two lines on a narrow window
 * and the bar is then twice as tall, so a hardcoded offset would be wrong on
 * exactly the screens where space is tightest. A ResizeObserver keeps the
 * padding equal to whatever the bar actually is, and takes it away again when
 * the session ends.
 */
export const IMPERSONATOR_KEY = 'impersonator_token';

export function readImpersonation() {
  const t = localStorage.getItem('token');
  if (!t || !localStorage.getItem(IMPERSONATOR_KEY)) return null;
  try {
    const claims = JSON.parse(atob(t.split('.')[1]));
    return claims.imp ? { ...claims.imp, exp: claims.exp } : null;
  } catch {
    return null;
  }
}

/** Swap the session for one that resolves to `userId`, and reload into it. */
export async function startImpersonation(userId, reason, clientId) {
  const r = await api.post('/api/impersonation/start',
                           { user_id: userId, reason, client_id: clientId });
  // Keep the real session aside. Losing it would mean signing in again to
  // get back, which is how people end up staying in the wrong account.
  localStorage.setItem(IMPERSONATOR_KEY, localStorage.getItem('token') || '');
  localStorage.setItem('token', r.data.token);
  // The cached profile is still the administrator's. Leaving it meant the
  // first paint of the session you had just stepped into showed your own
  // name, role and menu — and if anything read `user` before the profile
  // fetch landed, it kept showing them.
  localStorage.removeItem('user');
  // WHERE THE SERVER SAYS, NOT `/`.
  //
  // A hardcoded /dashboard landed on Access Restricted for anybody whose role
  // cannot view it — which is every client role. `/` was the fix, and it was
  // worse: on this deployment `/` is the PUBLIC WEBSITE, so the administrator
  // was dropped on Portal Login holding a working view-as token, signed in
  // again, and was themselves. "I see my screen."
  //
  // /api/impersonation/start now resolves it from the TARGET's own
  // permissions, including /collect when they are a mobile collector,
  // because that is their screen.
  window.location.href = r.data.landing || '/dashboard';
  return r.data;
}

export default function ImpersonationBanner() {
  const [imp, setImp] = useState(readImpersonation);
  const [left, setLeft] = useState('');

  useEffect(() => {
    if (!imp) return undefined;
    const tick = () => {
      const secs = Math.max(0, (imp.exp || 0) - Math.floor(Date.now() / 1000));
      const m = Math.floor(secs / 60);
      setLeft(secs <= 0 ? 'expired' : `${m}m ${secs % 60}s left`);
      // The token dies on its own; walking the person out beats letting the
      // app start refusing reads with no explanation.
      if (secs <= 0) exit();
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imp]);

  const exit = async () => {
    let landing = '/dashboard';
    try {
      const r = await api.post('/api/impersonation/stop',
                               { session_key: imp?.session });
      landing = r?.data?.landing || landing;
    } catch { /* the record closes on expiry anyway */ }
    const mine = localStorage.getItem(IMPERSONATOR_KEY);
    if (mine) localStorage.setItem('token', mine);
    localStorage.removeItem(IMPERSONATOR_KEY);
    // ...and on the way back out, for the same reason in reverse.
    localStorage.removeItem('user');
    // NOT `/`. That is the public website on this deployment, which is how
    // leaving a view-as session looked like being signed out.
    window.location.href = landing;
  };

  // Keep the document clear of the bar by exactly the bar's own height.
  const barRef = useRef(null);
  useEffect(() => {
    if (!imp) return undefined;
    const el = barRef.current;
    if (!el) return undefined;
    const apply = () => {
      const h = el.offsetHeight;
      // The document, for ordinary flow content...
      document.body.style.paddingTop = `${h}px`;
      // ...and a variable for the chrome that is position:fixed and therefore
      // cannot be moved by padding anything. `.app-sidebar` reads it.
      document.documentElement.style.setProperty('--imp-bar', `${h}px`);
    };
    apply();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(apply);
      ro.observe(el);
    } else {
      window.addEventListener('resize', apply);
    }
    return () => {
      if (ro) ro.disconnect(); else window.removeEventListener('resize', apply);
      // Leaving either behind would push the whole application down for the
      // rest of the session, long after the bar had gone.
      document.body.style.paddingTop = '';
      document.documentElement.style.removeProperty('--imp-bar');
    };
  }, [imp]);

  if (!imp) return null;

  return (
    <div ref={barRef}
         style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 5000,
                  background: '#b45309', color: '#fff', display: 'flex',
                  alignItems: 'center', gap: 12, padding: '7px 16px',
                  fontSize: 12.5, boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                  flexWrap: 'wrap' }}>
      <Eye size={15} />
      <span>
        Viewing as <strong>{imp.as_name}</strong> — read only.
        Nothing can be changed while you are in their account.
      </span>
      <span style={{ opacity: 0.85 }}>{left}</span>
      <button onClick={exit}
        style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center',
                 gap: 6, border: 'none', background: '#fff', color: '#b45309',
                 borderRadius: 999, padding: '4px 14px', fontSize: 12,
                 fontWeight: 800, cursor: 'pointer' }}>
        <LogOut size={12} /> Back to my account
      </button>
    </div>
  );
}
